import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import type { MatchStolenResult } from './assets.mutation';

export type DuplicateKind = 'cross_owner' | 'same_owner_multi_listing' | 'same_listing';

export type DuplicateGroup = {
  phash: string;
  kind: DuplicateKind;
  severity: 'high' | 'low' | 'info';
  asset_count: number;
  owners: string[];
  eids: number[];
  asids: number[];
  match?: 'near';
  max_distance?: number;
};

export type DuplicatesResponse = {
  match: 'exact' | 'near';
  distance: number;
  cross_only: boolean;
  group_count: number;
  kinds: Partial<Record<DuplicateKind, number>>;
  coverage: { images: number; hashed: number; measured: number };
  groups: DuplicateGroup[];
  truncated: boolean;
};

export type AssetMatches = {
  asid: number;
  phash: string | null;
  owner?: string | null;
  reason?: string;
  matches: { asid: number; eid: number | null; owner: string | null; cross_owner: boolean; distance: number }[];
};

export type OffPlatform = {
  asid: number;
  image_url?: string;
  checked: boolean;
  reason?: string;
  matches: { url?: string; title?: string; source?: string; thumbnail?: string }[];
};

const unwrap = (raw: any) => (raw?.data && typeof raw.data === 'object' && !Array.isArray(raw.data) ? raw.data : raw ?? {});

export const KIND_LABELS: Record<DuplicateKind, string> = {
  cross_owner: 'Different sellers',
  same_owner_multi_listing: 'Same seller, several listings',
  same_listing: 'Same listing',
};

export const useDuplicateGroupsQuery = (match: 'exact' | 'near', distance: number, crossOnly: boolean) => {
  const request = useClientStore((s) => s.request);
  const get = withErrorHandling(async (): Promise<DuplicatesResponse> =>
    unwrap(await request(`/api/admin/assets/duplicates?match=${match}&distance=${distance}&cross_only=${crossOnly}&limit=200`)),
  'Could not load duplicates');
  return useQuery({ queryKey: ['asset-duplicates', match, distance, crossOnly], queryFn: get, placeholderData: keepPreviousData, staleTime: 60_000 });
};

export const useAssetMatchesQuery = (asid: number | undefined, distance: number) => {
  const request = useClientStore((s) => s.request);
  const get = withErrorHandling(async (): Promise<AssetMatches> =>
    unwrap(await request(`/api/admin/assets/${asid}/duplicates?distance=${distance}`)), 'Could not load matches');
  return useQuery({ queryKey: ['asset-duplicates', 'one', asid, distance], queryFn: get, enabled: asid != null });
};

export const useOffPlatformMutation = () => {
  const request = useClientStore((s) => s.request);
  return useMutation({
    mutationFn: withErrorHandling(async (asid: number): Promise<OffPlatform> =>
      unwrap(await request(`/api/admin/assets/${asid}/off-platform`, 'POST', { confirm_paid: true })), 'Web search failed'),
  });
};

/** Reverse-image lookup with an uploaded file (multipart; never stored). */
export const useMatchStolenUploadMutation = () => {
  const request = useClientStore((s) => s.request);
  return useMutation({
    mutationFn: withErrorHandling(async ({ file, max_hamming }: { file: File; max_hamming: number }): Promise<MatchStolenResult> => {
      const fd = new FormData();
      fd.append('image', file);
      fd.append('max_hamming', String(max_hamming));
      fd.append('limit', '50');
      const body = unwrap(await request('/api/admin/assets/match-stolen', 'POST', fd as any));
      return { query_phash: body.query_phash ?? '', max_hamming: body.max_hamming ?? max_hamming,
               match_count: body.match_count ?? (body.matches ?? []).length, matches: body.matches ?? [] };
    }, 'Reverse-image lookup failed'),
  });
};
